import { getTranslation } from '@mahawi/eshop-common/src/translation';
import {
  type ILanguageType,
  type IPropertyValue,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Button, Col, Divider, Form, Input, Row, Space, Spin } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { EMPTY_VALUE } from '../../fragments/empty-value';
import { AddFocusEventListener } from '../../fragments/form-events';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import {
  nameValueUpdate,
  propertyValueNameLive,
} from '../../reducers/property/reducer';
import { SPACING } from '../../theme/tokens';
import CopyToClipboard from '../copy-to-clipboard';
import GoogleTranslate from '../google-translate';
import GoogleTranslateAutomat, {
  type ITextTranslate,
} from '../google-translate-automat';
import StatusIcon from '../status-icon';

interface IForm {
  name: string;
}

function ValueName({
  propertyValue,
}: {
  propertyValue: IPropertyValue;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const languages = useSelector((s: RootState) => s.Language.languages);
  const propertyInProcess = useSelector((s: RootState) => s.Property.inProcess);

  const [form] = Form.useForm<IForm>();

  const [invalidLanguageTranslations, setInvalidLanguageTranslations] =
    useState<Set<string>>(new Set());
  const [valueNameEN, setValueNameEN] = useState<string>();
  const [gtLinkName, setGtLinkName] = useState<string>(
    'GT value name EN → ---',
  );
  const [gtLinkNameReversed, setGtLinkNameReversed] = useState<string>(
    'GT value name --- → EN',
  );
  const [valueName, setValueName] = useState<string>('');

  const propertyValueNamesJson: string = JSON.stringify(propertyValue.names);

  const translateName = useCallback(
    (languageTypeCode: string): void => {
      AddFocusEventListener(form, (text: string): undefined => {
        form.setFieldsValue({
          name: text,
        });
        dispatch(
          propertyValueNameLive(propertyValue.code, text, languageTypeCode),
        );
      });
    },
    [dispatch, form, propertyValue.code],
  );

  const onFinish: (values: IForm) => void = useCallback(
    ({ name }: IForm): void => {
      if (propertyValue.code && languageType) {
        dispatch(nameValueUpdate(propertyValue.code, name, languageType.code));
        dispatch(
          propertyValueNameLive(propertyValue.code, name, languageType.code),
        );
      }
    },
    [dispatch, languageType?.code, propertyValue.code],
  );

  useEffect((): void => {
    const valueNameUEEN: string | null = getTranslation(propertyValue.names);

    setValueNameEN(valueNameUEEN || EMPTY_VALUE);

    const valueNameUE: string | null = getTranslation(
      propertyValue.names,
      languageType,
    );

    setValueName(valueNameUE ? valueNameUE : '');

    const valueNameTranslatedUE: string = valueNameUE ? valueNameUE : '';

    form.setFieldsValue({
      name: valueNameTranslatedUE,
    });

    setGtLinkName(`GT value name EN → ${languageType?.code}`);
    setGtLinkNameReversed(`GT value name ${languageType?.code} → EN`);
  }, [
    languageType?.code,
    form,
    propertyValue,
    propertyValue.names,
    propertyValueNamesJson,
  ]);

  useEffect((): void => {
    const invalidLanguageTranslationsUE = new Set<string>();

    languages?.forEach((language: ILanguageType): void => {
      const cn: ITranslation | undefined = propertyValue.names.find(
        (p: ITranslation): boolean => p.code === language.code,
      );

      if (!cn || cn.value.length < 1) {
        invalidLanguageTranslationsUE.add(language.code);
      }
    });

    setInvalidLanguageTranslations(invalidLanguageTranslationsUE);
  }, [
    languages,
    languageType?.code,
    propertyValue.names,
    propertyValueNamesJson,
  ]);

  const valueNameTranslated: string =
    valueNameEN || `** ${propertyValue.code} **`;

  const texts: ITextTranslate[] = [
    {
      text: valueNameTranslated,
      callbacks: [
        (text: string): void => {
          form.setFieldValue('name', text);
        },
      ],
    },
  ];

  return (
    <>
      <Row gutter={SPACING.lg}>
        <Col span={24}>
          <Space size={SPACING.lg} align="start" orientation="horizontal">
            <strong>{propertyValue.code}</strong>
            <StatusIcon ok={invalidLanguageTranslations.size === 0} />
            {invalidLanguageTranslations.size > 0 && (
              <small>
                {[...invalidLanguageTranslations]
                  .map((code: string): string => code)
                  .join(', ')}
              </small>
            )}
          </Space>
        </Col>

        <Col span={24}>
          <Form
            form={form}
            onFinish={(values: IForm): void => onFinish(values)}
          >
            <Row>
              <Col xs={24} sm={12}>
                <Space size={SPACING.lg} align="start">
                  <Button
                    type="primary"
                    htmlType="submit"
                    disabled={propertyInProcess}
                  >
                    Submit
                  </Button>
                  <Spin size="small" spinning={propertyInProcess} />
                </Space>
              </Col>

              <Col span={4}>
                <GoogleTranslateAutomat
                  texts={texts}
                  submit={form.submit}
                  isSaving={propertyInProcess}
                />
              </Col>

              <Col span={4}>
                <GoogleTranslate
                  fromLanguageCode="EN"
                  toLanguageCode={languageType?.code}
                  name={gtLinkName}
                  text={valueNameTranslated}
                  onClick={(): void => {
                    if (!languageType) {
                      return;
                    }

                    translateName(languageType.code);
                  }}
                />
              </Col>

              <Col span={4}>
                <GoogleTranslate
                  fromLanguageCode={languageType?.code}
                  toLanguageCode="EN"
                  name={gtLinkNameReversed}
                  text={valueName}
                  onClick={(): void => translateName('EN')}
                />
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <Form.Item label="Name">
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item
                      name="name"
                      noStyle
                      rules={[
                        {
                          required: true,
                        },
                        {
                          type: 'string',
                          min: 2,
                        },
                      ]}
                    >
                      <Input placeholder="Name" disabled={propertyInProcess} />
                    </Form.Item>
                    <CopyToClipboard
                      text={form.getFieldValue('name') || ''}
                      size="small"
                      type="link"
                    />
                  </Space.Compact>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <Divider />
    </>
  );
}

export default ValueName;
